import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';

import { of } from 'rxjs';
import { catchError, filter, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';

import { RealtorService } from 'src/app/modules/core/services/realtor.service';

import { GrantSummary } from 'src/app/modules/core/models/realtor.model';

import * as RealtorActions from 'src/app/modules/ngrx-store/realtor/realtor.actions';
import { getPreviewRealtorEmail } from 'src/app/modules/ngrx-store/realtor/realtor.selectors';
import * as YourHomeActions from 'src/app/modules/ngrx-store/your-home/your-home.actions';
import { getHomeDetails, getSalesAgreementId } from 'src/app/modules/ngrx-store/your-home/your-home.selectors';

import { displayError } from 'src/app/modules/shared/utilities/notifications.utils';

@Injectable()
export class RealtorEffects {
	constructor(
		private actions$: Actions,
		private realtorService: RealtorService,
		private store: Store,
	) { }

	loadRealtorAccess$ = createEffect(() => this.actions$.pipe(
		ofType(
			YourHomeActions.loadHomeDetailsSuccess,
			RealtorActions.loadRealtorAccess,
			RealtorActions.removeRealtorAccessSuccess,    // reload after remove
			RealtorActions.reactivateRealtorAccessSuccess, // reload after reactivate
			RealtorActions.createRealtorGrantSuccess       // reload after create
		),
		withLatestFrom(this.store.select(getSalesAgreementId), this.store.select(getHomeDetails)),
		map(([, salesAgreementId, homeDetails]) => ({ salesAgreementId, homeDetails })),
		filter(({ salesAgreementId, homeDetails }) =>
			Boolean(salesAgreementId) &&
			Boolean(homeDetails?.realtor?.emailAssocs?.[0]?.email?.emailAddress)
		),
		switchMap(({ salesAgreementId, homeDetails }) => {
			const emailAddress = homeDetails?.realtor?.emailAssocs?.[0]?.email?.emailAddress;
			return this.realtorService.getGrants(emailAddress!, salesAgreementId).pipe(
				map((grant) => RealtorActions.loadRealtorAccessSuccess({ grant: grant[0] })),
				catchError((err) => of(RealtorActions.loadRealtorAccessFailure({ error: err })))
			);
		})
	));

	loadRealtorAccessFailure$ = createEffect(() => this.actions$.pipe(
		ofType(RealtorActions.loadRealtorAccessFailure),
		tap(({ error }) => {
			displayError(error);
		})
	),
	{ dispatch: false }
	);

	acknowledgeRealtorAccess$ = createEffect(() => this.actions$.pipe(
		ofType(RealtorActions.acknowledgeRealtorAccess),
		switchMap(({ grantId }) =>
			this.realtorService.acknowledgeAccess(grantId).pipe(
				map((grant) => RealtorActions.acknowledgeRealtorAccessSuccess({ grant })),
				catchError((err) => of(RealtorActions.acknowledgeRealtorAccessFailure({ error: err })))
			))
	));

	removeRealtorAccess$ = createEffect(() => this.actions$.pipe(
		ofType(RealtorActions.removeRealtorAccess),
		switchMap(({ grantId }) =>
			this.realtorService.removeAccess(grantId).pipe(
				map((grant) => RealtorActions.removeRealtorAccessSuccess({ grant })),
				catchError((err) => of(RealtorActions.removeRealtorAccessFailure({ error: err })))
			))
	));
	
	reactivateRealtorAccess$ = createEffect(() => this.actions$.pipe(
		ofType(RealtorActions.reactivateRealtorAccess),
		switchMap(({ grantId }) =>
			this.realtorService.reactivateAccess(grantId).pipe(
				map((grant) => RealtorActions.reactivateRealtorAccessSuccess({ grant })),
				catchError((err) => of(RealtorActions.reactivateRealtorAccessFailure({ error: err })))
			))
	));

	loadRealtorGrants$ = createEffect(() => this.actions$.pipe(
		ofType(RealtorActions.loadRealtorGrants, RealtorActions.loadRealtorGrantsForPreview),
		withLatestFrom(this.store.select(getPreviewRealtorEmail)),
		filter(([action, previewRealtorEmail]) =>
			action.type !== RealtorActions.loadRealtorGrantsForPreview.type || Boolean(previewRealtorEmail)
		),
		switchMap(([action, previewRealtorEmail]) =>
			this.realtorService.getGrants(
				action.type === RealtorActions.loadRealtorGrantsForPreview.type ? previewRealtorEmail : undefined
			).pipe(
				map((realtorGrants) => RealtorActions.loadRealtorGrantsSuccess({ realtorGrants })),
				catchError((err) => of(RealtorActions.loadRealtorGrantsFailure({ error: err })))
			)
		)
	));

	loadRealtorGrantsByEmail$ = createEffect(() => this.actions$.pipe(
		ofType(RealtorActions.loadRealtorGrantsByEmail),
		switchMap(({ realtorEmail }) =>
			this.realtorService.getGrants(realtorEmail).pipe(
				map(realtorGrants => RealtorActions.loadRealtorGrantsSuccess({ realtorGrants: realtorGrants as GrantSummary[] })),
				catchError(err => of(RealtorActions.loadRealtorGrantsFailure({ error: err })))
			)
		)
	));

	loadRealtorGrantsFailure$ = createEffect(() => this.actions$.pipe(
		ofType(RealtorActions.loadRealtorGrantsFailure),
		tap(({ error }) => {
			if (error instanceof HttpErrorResponse && error.status !== 404) {
				displayError(error);
			}
		})
	),
	{ dispatch: false }
	);

	createRealtorGrant$ = createEffect(() => this.actions$.pipe(
		ofType(RealtorActions.createRealtorGrant),
		switchMap(({ marketId, salesAgreementId, realtorEmail }) =>
			this.realtorService.createGrant(marketId, salesAgreementId, realtorEmail).pipe(
				map(() => RealtorActions.createRealtorGrantSuccess()),
				catchError((err) => of(RealtorActions.createRealtorGrantFailure({ error: err })))
			))
	));

	upsertVisit$ = createEffect(() => this.actions$.pipe(
		ofType(RealtorActions.upsertVisit),
		switchMap(({ marketId, salesAgreementId }) =>
			this.realtorService.upsertVisit(marketId, salesAgreementId).pipe(
				map((upsertedVisit) => RealtorActions.upsertVisitSuccess({ visit: upsertedVisit })),
				catchError((error) => of(RealtorActions.upsertVisitFailure({ error })))
			)
		)
	));
}